
import { Component, Prop, Vue } from 'vue-property-decorator';
import GoogleDriveSelector from '@/components/GoogleDriveSelector.vue';

interface PurchaseOrderAttachment {
  filename: string;
  googleDriveFileId: string;
  googleDriveLink: string;
  fileType: string;
  uploadedAt: string;
  uploadedBy?: string;
}

@Component({
  components: {
    GoogleDriveSelector
  }
})
export default class PurchaseOrderAttachments extends Vue {
  @Prop({ type: Number, required: true }) purchaseOrderId!: number;
  @Prop({ type: Array, default: () => [] }) attachments!: PurchaseOrderAttachment[];
  @Prop({ type: Boolean, default: true }) canEdit!: boolean;

  private removing: number | null = null;
  private showUploadDialog = false;

  // Document MIME types for filtering
  private readonly DOCUMENT_MIME_TYPES = [
    'application/pdf',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/csv',
    'application/vnd.google-apps.spreadsheet',
    'application/vnd.google-apps.document',
    'application/vnd.google-apps.presentation'
  ];

  private handleFilesSelected(files: PurchaseOrderAttachment[]) {
    this.$emit('attachments-added', files);
    this.$emit('show-message', `${files.length} file(s) attached successfully`, 'success');
  }

  private handleDialogFilesSelected(files: PurchaseOrderAttachment[]) {
    this.handleFilesSelected(files);
    this.closeDialog();
  }

  private closeDialog() {
    this.showUploadDialog = false;
    // Clear any selected files in the dialog
    const driveSelector = this.$refs.driveSelector as any;
    if (driveSelector) {
      driveSelector.selectedFilesMeta = [];
    }
  }

  private handleAccountChanged(account: string) {
    // Handle account changes if needed
  }

  private handleMessage(message: string, type: string) {
    this.$emit('show-message', message, type);
  }

  private async removeAttachment(index: number) {
    this.removing = index;
    try {
      this.$emit('attachment-removed', index);
      this.$emit('show-message', 'Attachment removed successfully', 'success');
    } catch (error) {
      console.error('Error removing attachment:', error);
      this.$emit('show-message', 'Error removing attachment', 'error');
    } finally {
      this.removing = null;
    }
  }

  // File type utility methods
  private getFileTypeIcon(mimeType: string): string {
    if (mimeType.includes('pdf')) return 'mdi-file-pdf-box';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'mdi-file-excel-box';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'mdi-file-word-box';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'mdi-file-powerpoint-box';
    if (mimeType.includes('csv')) return 'mdi-file-delimited-outline';
    return 'mdi-file-document-outline';
  }

  private getFileTypeColor(mimeType: string): string {
    if (mimeType.includes('pdf')) return 'red';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'green';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'blue';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'orange';
    if (mimeType.includes('csv')) return 'teal';
    return 'grey';
  }

  private formatFileType(mimeType: string): string {
    if (mimeType.includes('pdf')) return 'PDF';
    if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) return 'Excel';
    if (mimeType.includes('word') || mimeType.includes('document')) return 'Word';
    if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) return 'PowerPoint';
    if (mimeType.includes('csv')) return 'CSV';
    return 'Document';
  }

  private formatDate(dateString: string): string {
    return new Date(dateString).toLocaleDateString();
  }
}
